import React from "react";
import { Box, Tooltip } from "@mui/material";
import MDButton from "components/MDButton";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import VideocamIcon from "@mui/icons-material/Videocam";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";
import CallEndIcon from "@mui/icons-material/CallEnd";
import CallIcon from "@mui/icons-material/Call";
import ClearIcon from "@mui/icons-material/Clear";

export default function CallControls({
  darkMode,
  callActive,
  dialValue,
  onDialAudio,
  onDialVideo,
  onClearDial,
  isMuted,
  onToggleMic,
  canToggleVideo,
  isVideoEnabled,
  onToggleVideo,
  callType,
  onLeaveConference,
  onDisconnectCall,
  onDisconnectAmbient,
}) {
  return (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        justifyContent: "center",
        flexWrap: "wrap",
      }}
    >
      {!callActive && (
        <Tooltip title="Audio Call">
          <span>
            <MDButton
              variant="contained"
              onClick={onDialAudio}
              sx={{
                background:
                  "linear-gradient(135deg, #4caf50 0%, #45a049 50%, #388e3c 100%)",
                color: "#ffffff",
                textShadow: darkMode
                  ? "0 2px 4px rgba(0, 0, 0, 0.6)"
                  : "0 1px 2px rgba(0, 0, 0, 0.3)",
                borderRadius: "50%",
                width: 60,
                height: 60,
                p: 1,
                minWidth: "unset",
                boxShadow: darkMode
                  ? "0 8px 24px rgba(76, 175, 80, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.2)"
                  : "0 8px 24px rgba(76, 175, 80, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3)",
                transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  background:
                    "radial-gradient(circle, rgba(255, 255, 255, 0.2) 0%, transparent 70%)",
                  pointerEvents: "none",
                },
                "&:hover": {
                  transform: "translateY(-6px) scale(1.12)",
                  boxShadow: darkMode
                    ? "0 16px 40px rgba(76, 175, 80, 0.7), inset 0 1px 0 rgba(255, 255, 255, 0.3)"
                    : "0 16px 40px rgba(76, 175, 80, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.4)",
                  background:
                    "linear-gradient(135deg, #66bb6a 0%, #56b856 50%, #43a047 100%)",
                },
                "&:active": {
                  transform: "translateY(-2px) scale(1.05)",
                  boxShadow: darkMode
                    ? "0 6px 16px rgba(76, 175, 80, 0.4)"
                    : "0 6px 16px rgba(76, 175, 80, 0.3)",
                },
              }}
            >
              <CallIcon sx={{ fontSize: 36, fontWeight: "900" }} />
            </MDButton>
          </span>
        </Tooltip>
      )}

      {!callActive && (
        <Tooltip title="Video Call">
          <span>
            <MDButton
              variant="contained"
              onClick={onDialVideo}
              sx={{
                background:
                  "linear-gradient(135deg, #2196F3 0%, #1e88e5 50%, #1976D2 100%)",
                color: "#ffffff",
                textShadow: darkMode
                  ? "0 2px 4px rgba(0, 0, 0, 0.6)"
                  : "0 1px 2px rgba(0, 0, 0, 0.3)",
                borderRadius: "50%",
                width: 60,
                height: 60,
                p: 1,
                minWidth: "unset",
                boxShadow: darkMode
                  ? "0 8px 24px rgba(33, 150, 243, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.2)"
                  : "0 8px 24px rgba(33, 150, 243, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3)",
                transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  background:
                    "radial-gradient(circle, rgba(255, 255, 255, 0.2) 0%, transparent 70%)",
                  pointerEvents: "none",
                },
                "&:hover": {
                  transform: "translateY(-6px) scale(1.12)",
                  boxShadow: darkMode
                    ? "0 16px 40px rgba(33, 150, 243, 0.7), inset 0 1px 0 rgba(255, 255, 255, 0.3)"
                    : "0 16px 40px rgba(33, 150, 243, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.4)",
                  background:
                    "linear-gradient(135deg, #42a5f5 0%, #2196f3 50%, #1e88e5 100%)",
                },
                "&:active": {
                  transform: "translateY(-2px) scale(1.05)",
                  boxShadow: darkMode
                    ? "0 6px 16px rgba(33, 150, 243, 0.4)"
                    : "0 6px 16px rgba(33, 150, 243, 0.3)",
                },
              }}
            >
              <VideocamIcon sx={{ fontSize: 36, fontWeight: "900" }} />
            </MDButton>
          </span>
        </Tooltip>
      )}

      {!!dialValue && !callActive && (
        <Tooltip title="Clear Dial">
          <span>
            <MDButton
              variant="contained"
              onClick={onClearDial}
              sx={{
                background:
                  "linear-gradient(135deg, #ff0000 0%, #ff1744 50%, #d32f2f 100%)",
                color: "#ffffff",
                textShadow: darkMode
                  ? "0 2px 4px rgba(0, 0, 0, 0.6)"
                  : "0 1px 2px rgba(0, 0, 0, 0.3)",
                borderRadius: "50%",
                width: 60,
                height: 60,
                p: 1,
                minWidth: "unset",
                boxShadow: darkMode
                  ? "0 8px 24px rgba(255, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.2)"
                  : "0 8px 24px rgba(255, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3)",
                transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  background:
                    "radial-gradient(circle, rgba(255, 255, 255, 0.2) 0%, transparent 70%)",
                  pointerEvents: "none",
                },
                "&:hover": {
                  transform: "translateY(-6px) scale(1.12)",
                  boxShadow: darkMode
                    ? "0 16px 40px rgba(255, 0, 0, 0.7), inset 0 1px 0 rgba(255, 255, 255, 0.3)"
                    : "0 16px 40px rgba(255, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.4)",
                  background:
                    "linear-gradient(135deg, #ff3333 0%, #ff4444 50%, #ff5555 100%)",
                },
                "&:active": {
                  transform: "translateY(-2px) scale(1.05)",
                  boxShadow: darkMode
                    ? "0 6px 16px rgba(255, 0, 0, 0.4)"
                    : "0 6px 16px rgba(255, 0, 0, 0.3)",
                },
              }}
            >
              <ClearIcon sx={{ fontSize: 36, fontWeight: "900" }} />
            </MDButton>
          </span>
        </Tooltip>
      )}

      {callActive && !callType.isAmbient && (
        <Tooltip title={isMuted ? "Mic Off" : "Mic On"}>
          <span>
            <MDButton
              variant="contained"
              onClick={onToggleMic}
              sx={{
                background: isMuted
                  ? "linear-gradient(135deg, #f44336 0%, #ef5350 50%, #e53935 100%)"
                  : "linear-gradient(135deg, #4caf50 0%, #45a049 50%, #388e3c 100%)",
                color: "#ffffff",
                textShadow: darkMode
                  ? "0 2px 4px rgba(0, 0, 0, 0.6)"
                  : "0 1px 2px rgba(0, 0, 0, 0.3)",
                borderRadius: "50%",
                width: 60,
                height: 60,
                p: 1,
                minWidth: "unset",
                boxShadow: darkMode
                  ? `0 8px 24px rgba(${isMuted ? "244, 67, 54" : "76, 175, 80"}, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.2)`
                  : `0 8px 24px rgba(${isMuted ? "244, 67, 54" : "76, 175, 80"}, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3)`,
                transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  background:
                    "radial-gradient(circle, rgba(255, 255, 255, 0.2) 0%, transparent 70%)",
                  pointerEvents: "none",
                },
                "&:hover": {
                  transform: "translateY(-6px) scale(1.12)",
                  boxShadow: darkMode
                    ? `0 16px 40px rgba(${isMuted ? "244, 67, 54" : "76, 175, 80"}, 0.7), inset 0 1px 0 rgba(255, 255, 255, 0.3)`
                    : `0 16px 40px rgba(${isMuted ? "244, 67, 54" : "76, 175, 80"}, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.4)` ,
                  background: isMuted
                    ? "linear-gradient(135deg, #ef5350 0%, #e53935 50%, #c62828 100%)"
                    : "linear-gradient(135deg, #66bb6a 0%, #56b856 50%, #43a047 100%)",
                },
                "&:active": {
                  transform: "translateY(-2px) scale(1.05)",
                  boxShadow: darkMode
                    ? `0 6px 16px rgba(${isMuted ? "244, 67, 54" : "76, 175, 80"}, 0.4)`
                    : `0 6px 16px rgba(${isMuted ? "244, 67, 54" : "76, 175, 80"}, 0.3)` ,
                },
              }}
            >
              {isMuted ? (
                <MicOffIcon sx={{ fontSize: 36, fontWeight: "900" }} />
              ) : (
                <MicIcon sx={{ fontSize: 36, fontWeight: "900" }} />
              )}
            </MDButton>
          </span>
        </Tooltip>
      )}

      {callActive && canToggleVideo && (
        <Tooltip title={isVideoEnabled ? "Video On" : "Video Off"}>
          <span>
            <MDButton
              variant="contained"
              onClick={onToggleVideo}
              sx={{
                background: isVideoEnabled
                  ? "linear-gradient(135deg, #2196F3 0%, #1e88e5 50%, #1976D2 100%)"
                  : "linear-gradient(135deg, #9e9e9e 0%, #757575 50%, #616161 100%)",
                color: "#ffffff",
                textShadow: darkMode
                  ? "0 2px 4px rgba(0, 0, 0, 0.6)"
                  : "0 1px 2px rgba(0, 0, 0, 0.3)",
                borderRadius: "50%",
                width: 60,
                height: 60,
                p: 1,
                minWidth: "unset",
                boxShadow: darkMode
                  ? "0 8px 24px rgba(33, 150, 243, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.2)"
                  : "0 8px 24px rgba(33, 150, 243, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3)",
                transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  background:
                    "radial-gradient(circle, rgba(255, 255, 255, 0.2) 0%, transparent 70%)",
                  pointerEvents: "none",
                },
                "&:hover": {
                  transform: "translateY(-6px) scale(1.12)",
                  boxShadow: darkMode
                    ? "0 16px 40px rgba(33, 150, 243, 0.7), inset 0 1px 0 rgba(255, 255, 255, 0.3)"
                    : "0 16px 40px rgba(33, 150, 243, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.4)",
                },
                "&:active": {
                  transform: "translateY(-2px) scale(1.05)",
                  boxShadow: darkMode
                    ? "0 6px 16px rgba(33, 150, 243, 0.4)"
                    : "0 6px 16px rgba(33, 150, 243, 0.3)",
                },
              }}
            >
              {isVideoEnabled ? (
                <VideocamIcon sx={{ fontSize: 36, fontWeight: "900" }} />
              ) : (
                <VideocamOffIcon sx={{ fontSize: 36, fontWeight: "900" }} />
              )}
            </MDButton>
          </span>
        </Tooltip>
      )}

      {callActive && !callType.isP2P && !callType.isAmbient && (
        <Tooltip title="Leave Call">
          <span>
            <MDButton
              variant="contained"
              onClick={onLeaveConference}
              sx={{
                background:
                  "linear-gradient(135deg, #f44336 0%, #e53935 50%, #c62828 100%)",
                color: "#ffffff",
                textShadow: darkMode
                  ? "0 2px 4px rgba(0, 0, 0, 0.6)"
                  : "0 1px 2px rgba(0, 0, 0, 0.3)",
                borderRadius: "50%",
                width: 60,
                height: 60,
                p: 1,
                minWidth: "unset",
                boxShadow: darkMode
                  ? "0 8px 24px rgba(244, 67, 54, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.2)"
                  : "0 8px 24px rgba(244, 67, 54, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3)",
                transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  background:
                    "radial-gradient(circle, rgba(255, 255, 255, 0.2) 0%, transparent 70%)",
                  pointerEvents: "none",
                },
                "&:hover": {
                  transform: "translateY(-6px) scale(1.12)",
                  boxShadow: darkMode
                    ? "0 16px 40px rgba(244, 67, 54, 0.7), inset 0 1px 0 rgba(255, 255, 255, 0.3)"
                    : "0 16px 40px rgba(244, 67, 54, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.4)",
                  background:
                    "linear-gradient(135deg, #ef5350 0%, #e53935 50%, #c62828 100%)",
                },
                "&:active": {
                  transform: "translateY(-2px) scale(1.05)",
                  boxShadow: darkMode
                    ? "0 6px 16px rgba(244, 67, 54, 0.4)"
                    : "0 6px 16px rgba(244, 67, 54, 0.3)",
                },
              }}
            >
              <CallEndIcon sx={{ fontSize: 36, fontWeight: "900" }} />
            </MDButton>
          </span>
        </Tooltip>
      )}

      {callActive && callType.isP2P && !callType.isAmbient && (
        <Tooltip title="Disconnect Call">
          <span>
            <MDButton
              variant="contained"
              onClick={onDisconnectCall}
              sx={{
                background:
                  "linear-gradient(135deg, #f44336 0%, #e53935 50%, #c62828 100%)",
                color: "#ffffff",
                textShadow: darkMode
                  ? "0 2px 4px rgba(0, 0, 0, 0.6)"
                  : "0 1px 2px rgba(0, 0, 0, 0.3)",
                borderRadius: "50%",
                width: 60,
                height: 60,
                p: 1,
                minWidth: "unset",
                boxShadow: darkMode
                  ? "0 8px 24px rgba(244, 67, 54, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.2)"
                  : "0 8px 24px rgba(244, 67, 54, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3)",
                transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  background:
                    "radial-gradient(circle, rgba(255, 255, 255, 0.2) 0%, transparent 70%)",
                  pointerEvents: "none",
                },
                "&:hover": {
                  transform: "translateY(-6px) scale(1.12)",
                  boxShadow: darkMode
                    ? "0 16px 40px rgba(244, 67, 54, 0.7), inset 0 1px 0 rgba(255, 255, 255, 0.3)"
                    : "0 16px 40px rgba(244, 67, 54, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.4)",
                  background:
                    "linear-gradient(135deg, #ef5350 0%, #e53935 50%, #c62828 100%)",
                },
                "&:active": {
                  transform: "translateY(-2px) scale(1.05)",
                  boxShadow: darkMode
                    ? "0 6px 16px rgba(244, 67, 54, 0.4)"
                    : "0 6px 16px rgba(244, 67, 54, 0.3)",
                },
              }}
            >
              <CallEndIcon sx={{ fontSize: 36, fontWeight: "900" }} />
            </MDButton>
          </span>
        </Tooltip>
      )}

      {callActive && callType.isAmbient && (
        <Tooltip title="Disconnect Call">
          <span>
            <MDButton
              variant="contained"
              onClick={onDisconnectAmbient}
              sx={{
                background:
                  "linear-gradient(135deg, #f44336 0%, #e53935 50%, #c62828 100%)",
                color: "#ffffff",
                textShadow: darkMode
                  ? "0 2px 4px rgba(0, 0, 0, 0.6)"
                  : "0 1px 2px rgba(0, 0, 0, 0.3)",
                borderRadius: "50%",
                width: 60,
                height: 60,
                p: 1,
                minWidth: "unset",
                boxShadow: darkMode
                  ? "0 8px 24px rgba(244, 67, 54, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.2)"
                  : "0 8px 24px rgba(244, 67, 54, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3)",
                transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  background:
                    "radial-gradient(circle, rgba(255, 255, 255, 0.2) 0%, transparent 70%)",
                  pointerEvents: "none",
                },
                "&:hover": {
                  transform: "translateY(-6px) scale(1.12)",
                  boxShadow: darkMode
                    ? "0 16px 40px rgba(244, 67, 54, 0.7), inset 0 1px 0 rgba(255, 255, 255, 0.3)"
                    : "0 16px 40px rgba(244, 67, 54, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.4)",
                  background:
                    "linear-gradient(135deg, #ef5350 0%, #e53935 50%, #c62828 100%)",
                },
                "&:active": {
                  transform: "translateY(-2px) scale(1.05)",
                  boxShadow: darkMode
                    ? "0 6px 16px rgba(244, 67, 54, 0.4)"
                    : "0 6px 16px rgba(244, 67, 54, 0.3)",
                },
              }}
            >
              <CallEndIcon sx={{ fontSize: 36, fontWeight: "900" }} />
            </MDButton>
          </span>
        </Tooltip>
      )}
    </Box>
  );
}
